/**
 * Platform Detection Utilities
 *
 * Provides utilities for detecting the current platform (iOS, Android, Web)
 * and handling platform-specific behaviors in a Capacitor hybrid app.
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/**
 * Platform detection utilities
 */
export const Platform = {
  /**
   * Check if running as a native app (iOS or Android)
   */
  isNative: (): boolean => Capacitor.isNativePlatform(),

  /**
   * Check if running in a web browser
   */
  isWeb: (): boolean => !Capacitor.isNativePlatform(),

  /**
   * Check if running on iOS
   */
  isIOS: (): boolean => Capacitor.getPlatform() === 'ios',

  /**
   * Check if running on Android
   */
  isAndroid: (): boolean => Capacitor.getPlatform() === 'android',

  /**
   * Get the current platform name
   */
  getPlatform: (): 'ios' | 'android' | 'web' => Capacitor.getPlatform() as 'ios' | 'android' | 'web',

  /**
   * Check if running inside Wix Dashboard
   */
  isWix: (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.location.search.includes('instance=');
  },

  /**
   * Check if running in a mobile browser (not native app)
   */
  isMobileBrowser: (): boolean => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      window.navigator.userAgent
    );
  },
};

/**
 * Open a URL in the system browser (for native) or new tab (for web)
 *
 * @param url - The URL to open
 */
export const openExternalUrl = async (url: string): Promise<void> => {
  if (Platform.isNative()) {
    await Browser.open({ url });
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Open the purchase/pricing page
 * On native: Opens in system browser (companion app model)
 * On web: Navigates to the pricing page
 *
 * @param returnUrl - Optional URL to return to after purchase
 */
export const openPurchasePage = async (returnUrl?: string): Promise<void> => {
  const baseUrl = import.meta.env.VITE_APP_URL || 'https://contentsage.app';
  const pricingUrl = returnUrl
    ? `${baseUrl}/pricing?return_url=${encodeURIComponent(returnUrl)}`
    : `${baseUrl}/pricing`;

  if (Platform.isNative()) {
    // On native, open in external browser (companion app model)
    await Browser.open({ url: pricingUrl });
  } else {
    // On web, navigate directly
    window.location.href = pricingUrl;
  }
};

/**
 * Get the API base URL based on platform
 * Native apps use absolute URLs, web uses relative
 */
export const getApiBaseUrl = (): string => {
  if (Platform.isNative()) {
    return import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';
  }
  // Web can use relative URLs (handled by proxy in dev, same origin in prod)
  return import.meta.env.VITE_API_URL || '/api/v1';
};

/**
 * Check if in-app purchases should be disabled
 * True for native apps (companion app model - purchases on web only)
 */
export const shouldDisableInAppPurchase = (): boolean => {
  return Platform.isNative();
};

/**
 * Get safe area insets for notched devices
 * Returns CSS variables or defaults
 */
export const getSafeAreaInsets = () => {
  if (typeof window === 'undefined') {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }

  const style = getComputedStyle(document.documentElement);
  return {
    top: parseInt(style.getPropertyValue('--safe-area-top') || '0', 10),
    bottom: parseInt(style.getPropertyValue('--safe-area-bottom') || '0', 10),
    left: parseInt(style.getPropertyValue('--safe-area-left') || '0', 10),
    right: parseInt(style.getPropertyValue('--safe-area-right') || '0', 10),
  };
};

export default Platform;
